import { Button } from "@mantine/core";
import { useRouter, type ErrorComponentProps } from "@tanstack/react-router";

import { ApiError, TransportError, isSessionExpired } from "../api/errors";
import { StatusScreen } from "../ui/StatusScreen";
import { errorReference, toUserMessage } from "./messages";
import { NotFoundScreen } from "./screens";
import { returnPath } from "./session";

/** What a route shows when its loading fails: one screen per kind of error, never a blank page. */
export function RouteErrorScreen({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  if (error instanceof ApiError && error.status === 404) return <NotFoundScreen />;
  if (isSessionExpired(error)) {
    return (
      <StatusScreen
        title="Your session ended"
        message="Sign in again to continue where you were."
        action={
          <Button
            onClick={() =>
              void router.navigate({
                to: "/sign-in",
                search: { redirect: returnPath(router.state.location) },
              })
            }
          >
            Sign in
          </Button>
        }
      />
    );
  }
  const retry = (): void => {
    reset();
    void router.invalidate();
  };
  return (
    <StatusScreen
      title={error instanceof TransportError ? "Can't load this page" : "Something went wrong"}
      message={toUserMessage(error)}
      reference={errorReference(error)}
      action={<Button onClick={retry}>Try again</Button>}
    />
  );
}
